import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import Logo from '../ui/Logo';
import {
  ArrowRightOnRectangleIcon,
  ChevronDownIcon,
  Squares2X2Icon,
} from '@heroicons/react/24/outline';
import { getPlatform } from '../../constants/platforms';
import PlatformLogo from '../ui/PlatformLogo';
import QuickCommerceLogo from '../ui/QuickCommerceLogo';
import { SELLERCLUB_NAV } from '../../constants/sellerclubBrand';
import {
  getStoreHubNavSections,
  hasStoreHubAdminNav,
  isStoreHubNavItemActive,
} from '../../constants/storeHubNav';

export default function StoreHubSidebar({
  user,
  platformFilter,
  onPlatformFilter,
  onLogout,
  isDarkMode,
  qcHubActive = false,
  qcStoreCount = null,
  amazonTotalCount = null,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [adminOpen, setAdminOpen] = useState(false);

  const navSections = useMemo(
    () => getStoreHubNavSections(user, { qcHubActive }),
    [user, qcHubActive]
  );

  const showAdminMenu = hasStoreHubAdminNav(user, { qcHubActive });

  const adminRouteActive = useMemo(
    () =>
      navSections.some((section) =>
        section.items.some((item) =>
          isStoreHubNavItemActive(location.pathname, searchParams, item.path)
        )
      ),
    [navSections, location.pathname, searchParams]
  );

  const onStoresHub =
    location.pathname === '/stores' || location.pathname === '/';

  useEffect(() => {
    if (adminRouteActive) {
      setAdminOpen(true);
    } else if (onStoresHub) {
      setAdminOpen(false);
    }
  }, [adminRouteActive, onStoresHub]);

  const amazonCount = amazonTotalCount ?? 0;
  const amazonPlatform = getPlatform('amazon');

  const shell = isDarkMode
    ? 'bg-slate-950 border-slate-800/90'
    : 'bg-white border-slate-200/90';
  const muted = isDarkMode ? 'text-slate-500' : 'text-slate-500';
  const text = isDarkMode ? 'text-slate-100' : 'text-slate-900';
  const sectionLabel = `text-[11px] font-semibold uppercase tracking-[0.12em] ${muted}`;

  const amazonActive = !qcHubActive && (platformFilter === 'amazon' || platformFilter === 'all');

  const adminEmphasis = adminOpen || adminRouteActive;

  const roleLabel = user?.role?.replace(/_/g, ' ') ?? '';

  return (
    <aside className={`w-[17.5rem] flex-shrink-0 border-r flex flex-col ${shell}`}>
      <Logo
        variant="sidebar"
        isDarkMode={isDarkMode}
        linkToDashboard={false}
        className={isDarkMode ? 'border-slate-800/90 border-r-0' : 'border-slate-200 border-r-0'}
      />

      <div className="flex-1 overflow-y-auto px-3 py-4 space-y-6">
        <div className="px-1">
          <p className={sectionLabel}>Marketplace</p>
          <div className="mt-2.5 space-y-1">
            <FilterBtn
              active={amazonActive}
              onClick={() => onPlatformFilter('amazon')}
              label={`${amazonPlatform.shortName}`}
              sublabel={`${amazonCount} store${amazonCount === 1 ? '' : 's'}`}
              platformId="amazon"
              isDarkMode={isDarkMode}
            />
            <FilterBtn
              active={qcHubActive}
              onClick={() => onPlatformFilter('quick_commerce')}
              label="Quick Commerce"
              sublabel={
                qcStoreCount != null
                  ? `${qcStoreCount} workspace${qcStoreCount === 1 ? '' : 's'}`
                  : null
              }
              platformId="quick_commerce"
              isDarkMode={isDarkMode}
            />
          </div>
        </div>

        {showAdminMenu ? (
          <div className="px-1">
            <div className="relative">
              <button
                type="button"
                aria-expanded={adminOpen}
                aria-haspopup="true"
                onClick={() => setAdminOpen((o) => !o)}
                className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                  adminEmphasis
                    ? isDarkMode
                      ? 'bg-slate-800/80 text-slate-100 ring-1 ring-inset ring-orange-500/30'
                      : 'bg-white text-slate-900 ring-1 ring-inset ring-orange-200 shadow-sm'
                    : isDarkMode
                      ? 'text-slate-300 hover:bg-slate-800/60 hover:text-white ring-1 ring-transparent'
                      : 'text-slate-700 hover:bg-slate-50 ring-1 ring-transparent'
                }`}
              >
                <Squares2X2Icon
                  className={`h-[18px] w-[18px] shrink-0 ${
                    adminEmphasis
                      ? isDarkMode
                        ? 'text-orange-400'
                        : 'text-orange-600'
                      : isDarkMode
                        ? 'text-slate-500'
                        : 'text-slate-500'
                  }`}
                />
                <span className="flex-1 text-left truncate">Menu</span>
                <ChevronDownIcon
                  className={`h-4 w-4 shrink-0 transition-transform duration-200 ${
                    adminOpen ? 'rotate-180' : ''
                  } ${adminEmphasis ? (isDarkMode ? 'text-slate-400' : 'text-slate-500') : muted}`}
                />
              </button>

              {adminOpen ? (
                <div
                  className={`mt-1.5 rounded-xl border overflow-hidden ${
                    isDarkMode
                      ? 'border-slate-700/80 bg-slate-900/95 ring-1 ring-orange-500/10'
                      : 'border-slate-200 bg-white shadow-sm'
                  }`}
                >
                  {navSections.map((section, sIdx) => (
                    <div key={section.id}>
                      {sIdx > 0 ? (
                        <div
                          className={`mx-3 border-t ${isDarkMode ? 'border-slate-800' : 'border-slate-100'}`}
                        />
                      ) : null}
                      {section.label ? (
                        <div className={`px-3 pt-2.5 pb-1 ${sectionLabel}`}>{section.label}</div>
                      ) : null}
                      <div className="pb-1.5">
                        {section.items.map((item) => {
                          const active = isStoreHubNavItemActive(
                            location.pathname,
                            searchParams,
                            item.path
                          );
                          const Icon = item.icon;
                          return (
                            <button
                              key={item.key}
                              type="button"
                              onClick={() => navigate(item.path)}
                              className={`group w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left transition-colors ${
                                active
                                  ? isDarkMode
                                    ? SELLERCLUB_NAV.activeChildDark
                                    : SELLERCLUB_NAV.activeChildLight
                                  : isDarkMode
                                    ? SELLERCLUB_NAV.inactiveChildDark
                                    : SELLERCLUB_NAV.inactiveChildLight
                              }`}
                            >
                              <Icon
                                className={`h-4 w-4 shrink-0 ${
                                  active
                                    ? isDarkMode
                                      ? SELLERCLUB_NAV.iconActiveDark
                                      : SELLERCLUB_NAV.iconActiveLight
                                    : isDarkMode
                                      ? SELLERCLUB_NAV.iconInactiveDark
                                      : SELLERCLUB_NAV.iconInactiveLight
                                }`}
                              />
                              <span className="truncate">{item.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>

      <div
        className={`shrink-0 border-t px-3 py-3 space-y-2 ${
          isDarkMode ? 'border-slate-800/90 bg-slate-950/50' : 'border-slate-200 bg-slate-50/80'
        }`}
      >
        <div className="flex items-center gap-2.5 px-2 py-1">
          <div
            className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-semibold ring-2 ring-orange-500/30 ${
              isDarkMode ? 'bg-slate-800 text-orange-300' : 'bg-white text-orange-800 shadow-sm'
            }`}
          >
            {user?.firstName?.[0]}
            {user?.lastName?.[0]}
          </div>
          <div className="min-w-0 flex-1">
            <div className={`text-sm font-medium truncate ${text}`}>
              {user?.firstName} {user?.lastName}
            </div>
            <div className={`text-xs capitalize truncate ${muted}`}>{roleLabel}</div>
          </div>
        </div>
        <button
          type="button"
          onClick={onLogout}
          className={`w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
            isDarkMode
              ? 'text-slate-400 hover:text-red-400 hover:bg-red-500/10'
              : 'text-slate-600 hover:text-red-700 hover:bg-red-50'
          }`}
        >
          <ArrowRightOnRectangleIcon className="h-4 w-4" />
          Sign out
        </button>
      </div>
    </aside>
  );
}

function FilterBtn({ active, onClick, label, sublabel, platformId, isDarkMode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-all ${
        active
          ? isDarkMode
            ? 'bg-slate-800/95 text-white ring-1 ring-inset ring-orange-500/35 shadow-sm'
            : 'bg-slate-900 text-white shadow-sm ring-1 ring-orange-500/25'
          : isDarkMode
            ? 'text-slate-300 hover:bg-slate-800/70 hover:text-white ring-1 ring-transparent'
            : 'text-slate-700 hover:bg-orange-50/80 hover:text-slate-900 ring-1 ring-transparent'
      }`}
    >
      {platformId ? (
        <span
          className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
            active
              ? 'bg-white/95 ring-1 ring-slate-200/80'
              : isDarkMode
                ? 'bg-slate-800 ring-1 ring-slate-700/80'
                : 'bg-white ring-1 ring-slate-200/90 shadow-sm'
          }`}
        >
          {platformId === 'quick_commerce' ? (
            <QuickCommerceLogo size={22} />
          ) : (
            <PlatformLogo
              platformId={platformId}
              size={platformId === 'amazon' ? 18 : 20}
              variant={platformId === 'amazon' ? 'wordmark' : 'mark'}
            />
          )}
        </span>
      ) : null}
      <span className="min-w-0 flex-1">
        <span className={`block text-sm font-medium truncate ${active ? '' : ''}`}>{label}</span>
        {sublabel ? (
          <span
            className={`block text-xs truncate mt-0.5 ${
              active
                ? isDarkMode
                  ? 'text-slate-400'
                  : 'text-slate-300'
                : isDarkMode
                  ? 'text-slate-500'
                  : 'text-slate-500'
            }`}
          >
            {sublabel}
          </span>
        ) : null}
      </span>
    </button>
  );
}
